import { useMemo } from 'react'
import {
    query,
    collection,
    orderBy,
    limit,
    where,
    addDoc,
    updateDoc,
    doc,
    Timestamp,
} from 'firebase/firestore'
import { db } from '@/configs/firebase'
import useCollectionQuery from '@/hooks/useCollectionQuery'
import useAuthStore from '@/stores/useAuthStore'

export interface NotificationItem {
    body: string
    title: string
    link: string
    read: boolean
    id: string
    created_at: Timestamp
}

/** The employee's latest `limitCount` notifications plus their unseen count. */
export const useNotifications = (limitCount: number) => {
    const { employee } = useAuthStore()

    const { data, error, loading } = useCollectionQuery(
        `notifications-${employee?.employee_id}-${limitCount}`,
        query(
            collection(db, 'notifications'),
            where('employee_ids', 'array-contains-any', [
                employee?.employee_id,
            ]),
            orderBy('created_at', 'desc'),
            limit(limitCount)
        )
    )

    const { data: lastSeen } = useCollectionQuery(
        `last-seen-notifications-${employee?.employee_id}`,
        query(
            collection(db, 'last-seen-notifications'),
            where('employee_id', '==', employee?.employee_id),
            limit(1)
        )
    )

    const lastSeenDoc = lastSeen?.docs?.[0]
    const lastSeenTimestamp = lastSeenDoc?.data()?.last_seen

    const notifications: NotificationItem[] = useMemo(
        () =>
            data?.docs?.map((d) => ({
                ...(d.data() as NotificationItem),
                id: d.id,
            })) || [],
        [data]
    )

    const notSeenCount = useMemo(() => {
        // No `last_seen` yet: every notification is unseen.
        if (!lastSeenTimestamp) return notifications.length
        return notifications.filter(
            (n) => n.created_at.toMillis() > lastSeenTimestamp.toMillis()
        ).length
    }, [lastSeenTimestamp, notifications])

    const markSeen = () => {
        if (notSeenCount <= 0) return
        if (!lastSeenDoc?.id) {
            addDoc(collection(db, 'last-seen-notifications'), {
                employee_id: employee?.employee_id,
                last_seen: Timestamp.now(),
            })
        } else {
            updateDoc(doc(db, `last-seen-notifications/${lastSeenDoc.id}`), {
                ...lastSeenDoc.data(),
                last_seen: Timestamp.now(),
            })
        }
    }

    return { notifications, loading, error, notSeenCount, markSeen }
}
